<script>
  import { createEventDispatcher } from 'svelte';
  import { worker } from '../../stores/worker';
  import { workOrderStore } from '../../stores/work-order';
  import { projectStore } from '../../stores/project';

  const dispatch = createEventDispatcher();

  $: workers = $worker.workers || [];
  $: workOrders = $workOrderStore.items || [];
  $: projects = $projectStore.items || [];
  $: loading = $worker.isLoading;

  function handleAssignTask(workerId) {
    dispatch('assignTask', { workerId });
  }

  function handleSchedule(workerId) {
    dispatch('schedule', { workerId });
  }

  function handleLogTime(woId) {
    dispatch('logTime', { workOrderId: woId });
  }

  async function handleComplete(woId) {
    try {
      await workOrderStore.update(woId, { status: 'COMPLETED' });
    } catch (e) {
      console.error('Failed to complete work order', e);
    }
  }

  let search = '';
  let expanded = new Set();
  let filter = 'all';

  $: filtered = workers.filter(w => {
    if (filter === 'available' && w.availability_status !== 'AVAILABLE') return false;
    if (filter === 'busy' && w.availability_status !== 'BUSY') return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return w.name?.toLowerCase().includes(s) || w.email?.toLowerCase().includes(s) ||
           w.skills?.some(sk => sk.toLowerCase().includes(s));
  });

  function toggleExpand(id) {
    if (expanded.has(id)) expanded.delete(id);
    else expanded.add(id);
    expanded = expanded;
  }

  function getWorkerWorkOrders(workerId) {
    return workOrders.filter(wo =>
      wo.assigned_workers?.some(aw => aw.worker_id === workerId || aw.worker === workerId) ||
      wo.assigned_worker === workerId
    );
  }

  function getWorkerProjects(workerId) {
    const workerWOs = getWorkerWorkOrders(workerId);
    const projectIds = [...new Set(workerWOs.map(wo => wo.project))];
    return projects.filter(p => projectIds.includes(p.id));
  }

  function statusConfig(status) {
    if (status === 'AVAILABLE') return { label: 'Available', class: 'status-success', dot: 'dot-success' };
    if (status === 'BUSY') return { label: 'Busy', class: 'status-warning', dot: 'dot-warning' };
    if (status === 'ON_LEAVE') return { label: 'On Leave', class: 'status-default', dot: 'dot-default' };
    return { label: status || 'Unknown', class: 'status-default', dot: 'dot-default' };
  }

  function woStatusConfig(status) {
    if (status === 'COMPLETED') return { label: 'Done', class: 'wo-success' };
    if (status === 'IN_PROGRESS') return { label: 'Active', class: 'wo-active' };
    if (status === 'PENDING') return { label: 'Pending', class: 'wo-pending' };
    return { label: status || 'Unknown', class: 'wo-default' };
  }

  function formatRate(rate) {
    return rate ? `$${rate}/hr` : '-';
  }

  $: availableCount = workers.filter(w => w.availability_status === 'AVAILABLE').length;
  $: busyCount = workers.filter(w => w.availability_status === 'BUSY').length;
  $: totalHours = workOrders.reduce((sum, wo) => sum + (wo.actual_hours || 0), 0);
</script>

<div class="worker-hub">
  <!-- Command Bar -->
  <div class="command-bar">
    <div class="search-box">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="text" placeholder="Search workers or skills..." bind:value={search} />
    </div>
    <div class="filters">
      <button class:active={filter === 'all'} on:click={() => filter = 'all'}>All</button>
      <button class:active={filter === 'available'} on:click={() => filter = 'available'}>Available</button>
      <button class:active={filter === 'busy'} on:click={() => filter = 'busy'}>Busy</button>
    </div>
  </div>

  <!-- Quick Stats -->
  <div class="stats-row">
    <div class="stat">
      <span class="stat-value">{availableCount}</span>
      <span class="stat-label">Available</span>
    </div>
    <div class="stat">
      <span class="stat-value">{busyCount}</span>
      <span class="stat-label">Busy</span>
    </div>
    <div class="stat">
      <span class="stat-value">{filtered.length}</span>
      <span class="stat-label">Total</span>
    </div>
  </div>

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
    </div>
  {:else if filtered.length === 0}
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
      <p>No workers found</p>
    </div>
  {:else}
    <div class="worker-grid">
      {#each filtered as w (w.id)}
        {@const workerWOs = getWorkerWorkOrders(w.id)}
        {@const workerProjects = getWorkerProjects(w.id)}
        {@const isExpanded = expanded.has(w.id)}
        {@const status = statusConfig(w.availability_status)}
        {@const activeWOs = workerWOs.filter(wo => wo.status !== 'COMPLETED')}
        {@const completedWOs = workerWOs.filter(wo => wo.status === 'COMPLETED')}
        {@const totalWorkerHours = workerWOs.reduce((sum, wo) => sum + (wo.actual_hours || 0), 0)}

        <div class="worker-card" class:expanded={isExpanded}>
          <!-- Card Header -->
          <div class="card-header" on:click={() => toggleExpand(w.id)} on:keydown={() => {}} role="button" tabindex="0">
            <div class="header-main">
              <div class="worker-title">
                <div class="avatar-wrapper">
                  <div class="avatar">{w.name?.charAt(0).toUpperCase() || 'W'}</div>
                  <span class="status-indicator {status.dot}"></span>
                </div>
                <div class="title-text">
                  <h3>{w.name}</h3>
                  <div class="worker-meta">
                    <span class="status-tag {status.class}">{status.label}</span>
                    {#if activeWOs.length > 0}
                      <span class="active-count">{activeWOs.length} active task{activeWOs.length !== 1 ? 's' : ''}</span>
                    {/if}
                  </div>
                </div>
              </div>
            </div>
            <div class="header-right">
              <div class="rate-badge">{formatRate(w.hourly_rate)}</div>
              <svg class="expand-icon" class:rotated={isExpanded} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <!-- Expandable Content -->
          {#if isExpanded}
            <div class="card-content">
              <!-- Skills -->
              {#if w.skills?.length > 0}
                <div class="skills-section">
                  <span class="skills-label">Skills</span>
                  <div class="skills-list">
                    {#each w.skills as skill}
                      <span class="skill-tag">{typeof skill === 'object' ? skill.name || skill.skill || JSON.stringify(skill) : skill}</span>
                    {/each}
                  </div>
                </div>
              {/if}

              <!-- Performance Metrics -->
              <div class="metrics-row">
                <div class="metric">
                  <span class="metric-value">{totalWorkerHours}h</span>
                  <span class="metric-label">Hours Logged</span>
                </div>
                <div class="metric">
                  <span class="metric-value">{completedWOs.length}</span>
                  <span class="metric-label">Completed</span>
                </div>
                <div class="metric">
                  <span class="metric-value">{workerProjects.length}</span>
                  <span class="metric-label">Projects</span>
                </div>
              </div>

              <!-- Active Work Orders -->
              {#if activeWOs.length > 0}
                <div class="section">
                  <div class="section-header">
                    <h4>Current Tasks</h4>
                    <span class="section-count">{activeWOs.length}</span>
                  </div>
                  <div class="wo-list">
                    {#each activeWOs.slice(0, 4) as wo}
                      {@const woStatus = woStatusConfig(wo.status)}
                      {@const project = projects.find(p => p.id === wo.project)}
                      <div class="wo-item">
                        <div class="wo-main">
                          <span class="wo-title">{wo.title}</span>
                          <div class="wo-meta">
                            {#if project}
                              <span class="project-link">{project.name}</span>
                            {/if}
                            <span class="wo-status {woStatus.class}">{woStatus.label}</span>
                          </div>
                        </div>
                        <div class="wo-actions">
                          <button class="wo-action" title="Log time" on:click|stopPropagation={() => handleLogTime(wo.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          <button class="wo-action complete" title="Mark complete" on:click|stopPropagation={() => handleComplete(wo.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    {/each}
                    {#if activeWOs.length > 4}
                      <div class="more-link">+{activeWOs.length - 4} more</div>
                    {/if}
                  </div>
                </div>
              {:else}
                <div class="empty-section">No active tasks - ready for assignment</div>
              {/if}

              <!-- Contact Info (collapsed) -->
              <details class="details-section">
                <summary>
                  <span>Contact Information</span>
                  <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div class="details-content">
                  <div class="detail-row">
                    <span class="detail-label">Email</span>
                    <a href="mailto:{w.email}" class="detail-value link">{w.email || '-'}</a>
                  </div>
                  <div class="detail-row">
                    <span class="detail-label">Phone</span>
                    <a href="tel:{w.phone}" class="detail-value link">{w.phone || '-'}</a>
                  </div>
                </div>
              </details>

              <!-- Quick Actions -->
              <div class="actions">
                <button class="action-btn primary" on:click|stopPropagation={() => handleAssignTask(w.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 4v16m-8-8h16" />
                  </svg>
                  Assign Task
                </button>
                <button class="action-btn" on:click|stopPropagation={() => handleSchedule(w.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Schedule
                </button>
              </div>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .worker-hub {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* Command Bar */
  .command-bar {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }

  .search-box {
    flex: 1;
    min-width: 200px;
    max-width: 320px;
    position: relative;
  }

  .search-box svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: var(--text-muted);
  }

  .search-box input {
    padding-left: 38px;
    height: 36px;
  }

  .filters {
    display: flex;
    gap: 4px;
    background: var(--bg);
    padding: 3px;
    border-radius: var(--radius);
    border: 1px solid var(--border);
  }

  .filters button {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-muted);
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 100ms;
  }

  .filters button:hover { color: var(--text); }
  .filters button.active {
    background: #fff;
    color: var(--text);
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }

  /* Stats Row */
  .stats-row {
    display: flex;
    gap: 24px;
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
  }

  .stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .stat-value {
    font-size: 18px;
    font-weight: 600;
    color: var(--text);
    font-variant-numeric: tabular-nums;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  /* Loading & Empty States */
  .loading-state, .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    color: var(--text-muted);
  }

  .empty-state svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }

  /* Worker Grid */
  .worker-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* Worker Card */
  .worker-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    transition: border-color 150ms, box-shadow 150ms;
  }

  .worker-card:hover {
    border-color: #cbd5e1;
  }

  .worker-card.expanded {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }

  /* Card Header */
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    user-select: none;
    gap: 12px;
  }

  .header-main {
    flex: 1;
    min-width: 0;
  }

  .worker-title {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .avatar-wrapper {
    position: relative;
  }

  .avatar {
    width: 36px;
    height: 36px;
    background: #64748b;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
  }

  .status-indicator {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: 2px solid var(--bg-card);
  }

  .dot-success { background: #22c55e; }
  .dot-warning { background: #f59e0b; }
  .dot-default { background: #94a3b8; }

  .title-text h3 {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    margin: 0;
  }

  .worker-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }

  .status-tag {
    font-size: 10px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    text-transform: uppercase;
  }

  .status-success { background: #dcfce7; color: #166534; }
  .status-warning { background: #fef3c7; color: #92400e; }
  .status-default { background: #f1f5f9; color: #475569; }

  .active-count {
    font-size: 11px;
    color: var(--accent);
    font-weight: 500;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .rate-badge {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
    font-variant-numeric: tabular-nums;
  }

  .expand-icon {
    width: 16px;
    height: 16px;
    color: var(--text-muted);
    transition: transform 150ms;
  }

  .expand-icon.rotated {
    transform: rotate(180deg);
  }

  /* Card Content */
  .card-content {
    padding: 0 16px 16px;
    border-top: 1px solid var(--border);
  }

  /* Skills Section */
  .skills-section {
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
  }

  .skills-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    display: block;
    margin-bottom: 8px;
  }

  .skills-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .skill-tag {
    font-size: 11px;
    font-weight: 500;
    padding: 4px 8px;
    background: var(--bg);
    color: var(--text);
    border-radius: 4px;
  }

  /* Metrics Row */
  .metrics-row {
    display: flex;
    gap: 24px;
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
  }

  .metric {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .metric-value {
    font-size: 16px;
    font-weight: 600;
    color: var(--text);
    font-variant-numeric: tabular-nums;
  }

  .metric-label {
    font-size: 10px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  /* Section */
  .section {
    padding: 14px 0;
    border-bottom: 1px solid var(--border);
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .section-header h4 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    margin: 0;
  }

  .section-count {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    background: var(--bg);
    padding: 2px 6px;
    border-radius: 4px;
  }

  /* Work Order List */
  .wo-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .wo-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    background: var(--bg);
    border-radius: 4px;
    gap: 12px;
  }

  .wo-main {
    flex: 1;
    min-width: 0;
  }

  .wo-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .wo-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 2px;
    font-size: 11px;
    color: var(--text-muted);
  }

  .project-link {
    color: var(--text-muted);
  }

  .wo-status {
    font-size: 9px;
    font-weight: 600;
    padding: 1px 4px;
    border-radius: 3px;
    text-transform: uppercase;
  }

  .wo-success { background: #dcfce7; color: #166534; }
  .wo-active { background: #dbeafe; color: #1e40af; }
  .wo-pending { background: #fef3c7; color: #92400e; }
  .wo-default { background: #f1f5f9; color: #64748b; }

  /* Work Order Actions */
  .wo-actions {
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 100ms;
  }

  .wo-item:hover .wo-actions {
    opacity: 1;
  }

  .wo-action {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 100ms;
  }

  .wo-action:hover {
    background: #fff;
    border-color: var(--border);
    color: var(--text);
  }

  .wo-action.complete:hover {
    background: #dcfce7;
    border-color: #86efac;
    color: #166534;
  }

  .wo-action svg {
    width: 14px;
    height: 14px;
  }

  .more-link {
    font-size: 12px;
    color: var(--accent);
    padding: 6px 0;
    cursor: pointer;
  }

  .empty-section {
    font-size: 12px;
    color: var(--text-muted);
    padding: 16px 0;
    text-align: center;
  }

  /* Details Section */
  .details-section {
    padding: 10px 0;
  }

  .details-section summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    cursor: pointer;
    padding: 6px 0;
    list-style: none;
  }

  .details-section summary::-webkit-details-marker {
    display: none;
  }

  .details-section summary .chevron {
    width: 14px;
    height: 14px;
    transition: transform 150ms;
  }

  .details-section[open] summary .chevron {
    transform: rotate(180deg);
  }

  .details-content {
    padding-top: 10px;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  .detail-label {
    font-size: 11px;
    color: var(--text-muted);
  }

  .detail-value {
    font-size: 12px;
    color: var(--text);
  }

  .detail-value.link {
    text-decoration: none;
  }

  .detail-value.link:hover {
    color: var(--accent);
  }

  /* Actions */
  .actions {
    display: flex;
    gap: 8px;
    padding-top: 14px;
  }

  .action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: #fff;
    color: var(--text);
    cursor: pointer;
    transition: all 100ms;
  }

  .action-btn:hover {
    background: var(--bg);
  }

  .action-btn.primary {
    background: var(--primary);
    color: #fff;
    border-color: var(--primary);
  }

  .action-btn.primary:hover {
    background: var(--primary-hover);
  }

  .action-btn svg {
    width: 14px;
    height: 14px;
  }

  /* Mobile Responsive */
  @media (max-width: 767px) {
    .command-bar {
      flex-direction: column;
      align-items: stretch;
    }

    .search-box {
      max-width: none;
    }

    .filters {
      justify-content: center;
    }

    .stats-row {
      justify-content: space-between;
      gap: 12px;
    }

    .stat-value {
      font-size: 16px;
    }

    .metrics-row {
      gap: 16px;
    }

    .metric-value {
      font-size: 14px;
    }

    .actions {
      flex-direction: column;
    }
  }
</style>
